export interface StringsConfig {
  app: {
    title: string;
    copyright: string;
  };
  navigation: {
    refresh: string;
  };
  modal: {
    download: string;
    close: string;
    loading: string;
    loadError: string;
    noImage: string;
    downloadError: string;
    downloadFailed: string;
  };
  grid: {
    loading: string;
    imageError: string;
    processing: string;
    searchingPhoto: string;
    retrying: string;
  };
  errors: {
    loadPhotos: string;
    retry: string;
    failedToLoad: string;
    failedToLoadConfig: string;
    reasons: {
      title: string;
      auth: string;
      credentials: string;
      network: string;
    };
  };
  notFound: {
    title: string;
    code: string;
    message: string;
    goHome: string;
  };
  banner: {
    thumbnailFailure: string;
    dismiss: string;
    refresh: string;
  };
  downloadAll: {
    button: string;
    preparing: string;
    progress: string;
    complete: string;
    error: string;
    confirmTitle: string;
    confirmMessage: string;
    confirm: string;
    cancel: string;
  };
}

export interface FolderInfo {
  displayName: string;
  description: string;
  zipDownloadUrl?: string | null;
  zipFileSize?: string | null;
  zipLastUpdated?: string | null;
}

export interface FoldersConfig {
  folders: Record<string, FolderInfo>;
  defaultFolderId: string;
}

class ConfigService {
  private strings: StringsConfig | null = null;
  private folders: FoldersConfig | null = null;
  private stringsPromise: Promise<StringsConfig> | null = null;
  private foldersPromise: Promise<FoldersConfig> | null = null;

  private async fetchConfig<T>(path: string): Promise<T> {
    const response = await fetch(`${process.env.PUBLIC_URL || ''}/config/${path}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${path}: ${response.statusText}`);
    }
    return response.json();
  }

  async getStrings(): Promise<StringsConfig> {
    if (this.strings) {
      return this.strings;
    }

    if (!this.stringsPromise) {
      this.stringsPromise = this.fetchConfig<StringsConfig>('strings.json').then(config => {
        this.strings = config;
        return config;
      });
    }

    return this.stringsPromise;
  }

  async getFolders(): Promise<FoldersConfig> {
    if (this.folders) {
      return this.folders;
    }

    if (!this.foldersPromise) {
      this.foldersPromise = this.fetchConfig<FoldersConfig>('folders.json').then(config => {
        this.folders = config;
        return config;
      });
    }

    return this.foldersPromise;
  }

  async getFolderInfo(folderId: string): Promise<FolderInfo> {
    const foldersConfig = await this.getFolders();
    return foldersConfig.folders[folderId] || foldersConfig.folders.default;
  }

  async getDefaultFolderId(): Promise<string> {
    const foldersConfig = await this.getFolders();
    return foldersConfig.defaultFolderId;
  }

  async getAllFolders(): Promise<Record<string, FolderInfo>> {
    const foldersConfig = await this.getFolders();
    return foldersConfig.folders;
  }
}

export const configService = new ConfigService();